// The review tabs: one per group of resume sections. Dragging a tab
// reorders the sections of this one resume (AGP feedback) without touching
// the template — the resume's sectionOrder is what the output follows.

import { useRef, useState } from "react";
import {
  AlignLeft,
  Award,
  BriefcaseBusiness,
  FolderOpen,
  GraduationCap,
  UserRound,
  Users,
  Wrench,
} from "lucide-react";
import type { SectionKey } from "@agp/shared";
import { cn } from "@/lib/utils";


export type TabKey =
  | "candidate"
  | "consultant"
  | "summary"
  | "experience"
  | "education"
  | "skills"
  | "certifications"
  | "other";

export const TABS: Array<{ key: TabKey; label: string; icon: typeof UserRound }> = [
  { key: "candidate", label: "Candidate", icon: UserRound },
  { key: "consultant", label: "Consultant", icon: Users },
  { key: "summary", label: "Summary", icon: AlignLeft },
  { key: "experience", label: "Experience", icon: BriefcaseBusiness },
  { key: "education", label: "Education", icon: GraduationCap },
  { key: "skills", label: "Skills", icon: Wrench },
  { key: "certifications", label: "Certificates", icon: Award },
  { key: "other", label: "Other", icon: FolderOpen },
];

// Which output sections each tab stands for, so dragging a tab reorders
// the sections of this resume (AGP feedback) without touching the template.
export const TAB_SECTIONS: Record<TabKey, SectionKey[]> = {
  candidate: ["candidateReport", "personalDetails"],
  consultant: ["consultantSummary"],
  summary: ["summary"],
  experience: ["experience"],
  education: ["education"],
  skills: ["coreCompetencies", "technicalSkills"],
  certifications: ["certifications", "publications"],
  other: ["careerInterests", "otherSections"],
};

/** Tabs in the order the resume's sections go, template order otherwise. */
export function tabOrder(sectionOrder: SectionKey[] | undefined): typeof TABS {
  if (!sectionOrder || sectionOrder.length === 0) return TABS;
  const rank = (t: (typeof TABS)[number]) => {
    const idx = TAB_SECTIONS[t.key].map((k) => sectionOrder.indexOf(k)).filter((i) => i >= 0);
    return idx.length > 0 ? Math.min(...idx) : Number.POSITIVE_INFINITY;
  };
  return [...TABS].sort((a, b) => rank(a) - rank(b) || TABS.indexOf(a) - TABS.indexOf(b));
}

export function TabRail({
  active,
  onSelect,
  attention,
  order,
  onReorder,
}: {
  active: TabKey;
  onSelect: (k: TabKey) => void;
  attention: Partial<Record<TabKey, boolean>>;
  order: typeof TABS;
  onReorder: (keys: TabKey[]) => void;
}) {
  const [dragging, setDragging] = useState<TabKey | null>(null);
  const [over, setOver] = useState<TabKey | null>(null);
  // The drop handler reads a ref: the native drag events can arrive before
  // React has committed the state set in dragstart.
  const draggingRef = useRef<TabKey | null>(null);
  const drop = (target: TabKey) => {
    const source = draggingRef.current;
    if (!source || source === target) return;
    const keys = order.map((t) => t.key).filter((k) => k !== source);
    keys.splice(keys.indexOf(target), 0, source);
    onReorder(keys);
  };
  // Keyboard alternative to dragging: Shift+← / Shift+→ on a focused tab.
  const nudge = (key: TabKey, delta: -1 | 1) => {
    const keys = order.map((t) => t.key);
    const i = keys.indexOf(key);
    const j = i + delta;
    if (j < 0 || j >= keys.length) return;
    keys.splice(i, 1);
    keys.splice(j, 0, key);
    onReorder(keys);
  };
  // Renders as the header strip of the editor card (no card of its own) —
  // the tabs and the inputs share one container per the review-screen layout.
  return (
    <div className="border-b border-border p-1.5">
      <nav aria-label="Resume sections" className="flex gap-1 overflow-x-auto" title="Drag a tab (or press Shift+← / Shift+→ on it) to change the order of the sections in this resume">
        {order.map((tab) => (
          <button
            key={tab.key}
            type="button"
            draggable
            data-tab={tab.key}
            onDragStart={(e) => {
              draggingRef.current = tab.key;
              setDragging(tab.key);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", tab.key);
            }}
            onDragEnter={(e) => e.preventDefault()}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (over !== tab.key) setOver(tab.key);
            }}
            onDrop={(e) => {
              e.preventDefault();
              drop(tab.key);
              draggingRef.current = null;
              setDragging(null);
              setOver(null);
            }}
            onDragEnd={() => {
              draggingRef.current = null;
              setDragging(null);
              setOver(null);
            }}
            onClick={() => onSelect(tab.key)}
            onKeyDown={(e) => {
              if (e.shiftKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
                e.preventDefault();
                nudge(tab.key, e.key === "ArrowLeft" ? -1 : 1);
              }
            }}
            aria-current={active === tab.key ? "true" : undefined}
            className={cn(
              "relative flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2.5 text-[13.5px] font-medium transition-colors",
              active === tab.key
                ? "bg-brand-tint font-semibold text-brand-on"
                : "text-text-2 hover:bg-surface-2 hover:text-text",
              dragging === tab.key && "opacity-50",
              over === tab.key && dragging && dragging !== tab.key && "ring-2 ring-brand-500",
            )}
          >
            <tab.icon size={16} strokeWidth={1.75} aria-hidden />
            {tab.label}
            {attention[tab.key] && (
              <span
                className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-warn"
                title="Needs a look"
              />
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}
